import { useT } from "../i18n/hooks.ts";
import { Dialog } from "./Dialog.tsx";
import { InlineProgress } from "./InlineProgress.tsx";

/**
 * "Export copy…" (TopBar.tsx) opens this instead of downloading directly —
 * there are now two genuinely different things it could mean, so asking is
 * clearer than picking one silently:
 *   - .ehdz: this app's own portable backup (exportProject.ts) — round-trips
 *     back into this app exactly, nothing else can open it.
 *   - CSV .zip: the same files the original desktop app writes for its
 *     calculation engine (exportCsvProject.ts) — for the user to inspect,
 *     archive, or feed to the real EvalHid_cal.exe.
 *
 * `exporting`/`error`: real gap reported live — even after moving the actual
 * work off the main thread (exportProject.ts's Worker, a genuine multi-
 * second operation for a Duero-scale project), this dialog used to close
 * the instant a button was clicked, with zero sign anything was happening
 * until the file suddenly appeared — or, on failure, nothing at all beyond
 * the console. Both option buttons are disabled while either export is
 * running, not just the one that was clicked, so a second click can't start
 * a redundant second export.
 */
export function ExportChoiceDialog({
  onExportEhdz,
  onExportCsv,
  exporting,
  error,
  onClose,
}: {
  onExportEhdz: () => void;
  onExportCsv: () => void;
  exporting: "ehdz" | "csv" | null;
  error: string | null;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <Dialog title={t("exportChoice.title")} onClose={onClose}>
      <div className="export-choice">
        <button
          className="export-choice-option"
          onClick={onExportEhdz}
          disabled={exporting !== null}
        >
          <strong>{t("exportChoice.ehdzTitle")}</strong>
          <span>{t("exportChoice.ehdzDescription")}</span>
        </button>
        <button
          className="export-choice-option"
          onClick={onExportCsv}
          disabled={exporting !== null}
        >
          <strong>{t("exportChoice.csvTitle")}</strong>
          <span>{t("exportChoice.csvDescription")}</span>
        </button>
      </div>
      {exporting && <InlineProgress text={t("exportChoice.generating")} />}
      {error && <p className="error">{t("exportChoice.error", { error })}</p>}
      <div className="dialog-actions">
        <button className="button" onClick={onClose} disabled={exporting !== null}>
          {t("common.cancel")}
        </button>
      </div>
    </Dialog>
  );
}
